/*
 * Header / navigation behaviour.
 *
 * Registered as plain globals rather than Alpine.data() so load order never
 * matters: Alpine evaluates x-data expressions against the global scope, and
 * this file is deferred ahead of bundle.js. No external dependencies.
 */
(function () {
  'use strict';

  var config = {
    moneyFormat: '${{amount}}',
    predictiveUrl: '/search/suggest',
    cartAddUrl: '/cart/add.js'
  };

  /* Money ---------------------------------------------------------------- */

  function group(value, thousands, decimal, places) {
    var parts = Math.abs(value).toFixed(places).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    var sign = value < 0 ? '-' : '';
    return sign + whole + (places > 0 ? decimal + parts[1] : '');
  }

  function formatMoney(value) {
    if (value === null || value === undefined || value === '') return '';

    var amount =
      typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (isNaN(amount)) return String(value);

    return config.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, token) {
      switch (token) {
        case 'amount_no_decimals':
          return group(amount, ',', '.', 0);
        case 'amount_with_comma_separator':
          return group(amount, '.', ',', 2);
        case 'amount_no_decimals_with_comma_separator':
          return group(amount, '.', ',', 0);
        case 'amount_with_apostrophe_separator':
          return group(amount, "'", '.', 2);
        default:
          return group(amount, ',', '.', 2);
      }
    });
  }

  /* Focus helpers -------------------------------------------------------- */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
    ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function focusableIn(root) {
    if (!root) return [];
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  var scrollLocks = 0;

  function lockScroll(on) {
    scrollLocks = Math.max(0, scrollLocks + (on ? 1 : -1));
    document.body.style.overflow = scrollLocks > 0 ? 'hidden' : '';
  }

  /* Alpine component ------------------------------------------------------ */

  window.SPHeaderNav = function (options) {
    // Deliberately closure-scoped, not component properties: Alpine wraps
    // component state in a reactive proxy, and proxying DOM nodes breaks
    // identity checks (activeElement === el) and perturbs x-show transitions.
    var controller = null;
    var restoreFocus = null;
    var trapHandler = null;

    return {
      open: null,
      searchOpen: false,
      query: '',
      loading: false,
      results: { products: [], queries: [] },

      init: function () {
        var self = this;
        Object.keys(options || {}).forEach(function (key) {
          if (options[key]) config[key] = options[key];
        });

        this.query = '';

        this.$watch('$store.header.mobileMenuOpened', function (value) {
          self.applyOverlay(value, self.$refs.mobileDrawer);
          if (value) self.searchOpen = false;
        });

        this.$watch('searchOpen', function (value) {
          self.applyOverlay(value, self.$refs.searchDrawer);
        });
      },

      get hasQuery() {
        return this.query.trim().length > 0;
      },

      // Short, non-repetitive announcement for screen readers. The results list
      // itself is not a live region, so typing does not re-read every row.
      get statusMessage() {
        if (!this.hasQuery) return '';
        if (this.loading) return 'Searching';
        var count = this.results.products.length;
        if (count === 0) return 'No results for ' + this.query;
        return count === 1 ? '1 product found' : count + ' products found';
      },

      /* Desktop mega panels — click to open. Hover was removed: the header
         bar's padding sits between a nav item and its panel, so travelling to
         the panel left the item's hover region and closed it mid-move. */

      togglePanel: function (id) {
        this.open = this.open === id ? null : id;
      },

      closePanel: function (returnFocusTo) {
        if (this.open === null) return;
        this.open = null;
        if (returnFocusTo && typeof returnFocusTo.focus === 'function') returnFocusTo.focus();
      },

      // Close a panel once focus leaves the nav item entirely (keyboard tab-out).
      panelFocusOut: function (event, id) {
        var item = event.currentTarget;
        var next = event.relatedTarget;
        if (this.open !== id) return;
        if (next && item.contains(next)) return;
        this.open = null;
      },

      /* Overlays */

      applyOverlay: function (isOpen, element) {
        if (isOpen) {
          restoreFocus = document.activeElement;
          lockScroll(true);

          this.$nextTick(function () {
            // Alpine 3.12 (the version bundled in this theme) can apply the
            // :class binding that reveals the panel *after* $nextTick, and
            // focusing a still visibility:hidden element silently fails. Try
            // once, then retry on the next frame if focus did not take.
            var focusTarget = function () {
              var preferred = element ? element.querySelector('[data-sp-autofocus]') : null;
              var items = focusableIn(element);
              var target = preferred || items[0];
              if (!target) return true;
              target.focus();
              return document.activeElement === target;
            };

            if (!focusTarget()) {
              requestAnimationFrame(function () {
                focusTarget();
              });
            }

            trapHandler = function (event) {
              if (event.key !== 'Tab') return;
              var focusable = focusableIn(element);
              if (!focusable.length) return;

              var first = focusable[0];
              var last = focusable[focusable.length - 1];

              if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
              } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
              }
            };
            if (element) element.addEventListener('keydown', trapHandler);
          });
        } else {
          lockScroll(false);
          if (element && trapHandler) {
            element.removeEventListener('keydown', trapHandler);
            trapHandler = null;
          }
          if (restoreFocus && typeof restoreFocus.focus === 'function' && restoreFocus !== document.body) {
            restoreFocus.focus();
          }
          restoreFocus = null;
        }
      },

      openSearch: function () {
        this.open = null;
        this.$store.header.mobileMenuOpened = false;
        this.searchOpen = true;
      },

      closeSearch: function () {
        this.searchOpen = false;
      },

      clearSearch: function () {
        this.query = '';
        this.results = { products: [], queries: [] };
        if (this.$refs.searchInput) this.$refs.searchInput.focus();
      },

      /* Predictive search */

      runSearch: function () {
        var self = this;
        var term = this.query.trim();

        if (controller) controller.abort();

        if (!term) {
          this.results = { products: [], queries: [] };
          this.loading = false;
          return;
        }

        this.loading = true;
        controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

        var url =
          config.predictiveUrl +
          '.json?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product,query&resources[limit]=6';

        fetch(url, {
          signal: controller ? controller.signal : undefined,
          headers: { Accept: 'application/json' }
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Search request failed');
            return response.json();
          })
          .then(function (data) {
            var found =
              data && data.resources && data.resources.results ? data.resources.results : {};

            // x-for keys must be unique: Alpine throws and drops the whole list
            // on a duplicate, and the endpoint can repeat a url across queries.
            self.results = {
              products: (found.products || []).map(function (product, index) {
                var variantId = null;
                if (
                  product.variants &&
                  product.variants.length === 1 &&
                  product.variants[0].available
                ) {
                  variantId = product.variants[0].id;
                }
                return {
                  key: 'p' + index + '-' + (product.id || product.url || ''),
                  id: product.id,
                  title: product.title,
                  url: product.url,
                  image: product.featured_image ? product.featured_image.url : product.image || null,
                  price: formatMoney(product.price),
                  variantId: variantId
                };
              }),
              queries: (found.queries || []).map(function (item, index) {
                return { key: 'q' + index + '-' + (item.text || ''), text: item.text, url: item.url };
              })
            };
            self.loading = false;
          })
          .catch(function (error) {
            if (error && error.name === 'AbortError') return;
            self.results = { products: [], queries: [] };
            self.loading = false;
          });
      },

      /* Add to cart, reusing the theme's existing cart drawer store */

      addToCart: function (variantId, event) {
        var button = event && event.currentTarget;
        if (button) button.disabled = true;

        fetch(config.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Add to cart failed');
            return response.json();
          })
          .then(
            function () {
              var store =
                window.Alpine && window.Alpine.store ? window.Alpine.store('cartDrawer') : null;
              if (!store) return;
              if (typeof store.updateCartCount === 'function') store.updateCartCount();
              if (typeof store.updateCartContent === 'function') store.updateCartContent();
              // Same hand-off as the theme's `button` component: close the search
              // drawer and slide the cart open so the add is visible.
              this.closeSearch();
              store.showDrawer = true;
            }.bind(this)
          )
          .catch(function () {
            /* Leave the drawer open; the row still links through to the product. */
          })
          .then(function () {
            if (button) button.disabled = false;
          });
      }
    };
  };
})();
